import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseButton from '../CloseButton/CloseButton';
import SelectOption from '../SelectOption/SelectOption';
import styles from './CallToActionEditor.module.scss';
import { useTranslation } from 'react-i18next';
import Slider from 'rc-slider';
import cx from 'classnames';
import { useCallToActionContext } from '../../context/CallToActionContext';
import VideoService from '../../service/VideoService';
import { ToastNotification } from '../Toast/Toast';
import { CustomButtonTooltip } from '../ButtonTooltip/ButtonTooltip';
import { getCurrentActions, isHexColor } from '../../utils/CallToActionHelper';
import { checkIsFullyTransparent } from '../../utils/colorHelper';
import { useUserContext } from '../../context/UserContext';
import { generateCallActionPng } from '../../utils/SimulatedItemsUtility';
import {
  INITIAL_ACTION_SETTINGS,
  MAX_RADIUS,
  MAX_SIZE,
  MIN_RADIUS,
  MIN_SIZE,
  POSITIONS,
  STEP_FROM_ACTION_OFFSET,
  DEFAULT_RADIUS,
} from '../../constants/callToActions';
import CallToActionsList from '../../features/CallToAction/components/CallToActionsList/CallToActionsList';
import { useVideoProgressContext } from '../../context/VideoProgressContext';
import { BACKSPACE_KEY_CODE } from 'engage/constants/common';
import { BrandingColorsSelection } from '../BrandColorsSelection/BrandColorsSelection';

const TEXT_BOX_TYPE = 'textBox';
const CALL_TO_ACTION_TYPE = 'callToAction';
const CARD_TYPE = 'textBoxCard';

const fontAdditionalColors = ['#ffffff', '#000000'];

const TYPES = [
  { title: 'text_box.editor.title', id: TEXT_BOX_TYPE },
  { title: 'call_to_action.editor.title', id: CALL_TO_ACTION_TYPE },
  { title: 'text_box.editor.card.title', id: CARD_TYPE },
];

const CallToActionEditor = ({ storyId, videoDuration, miniSidebar, checkClosing, videoDimensions, onCloseEditor }) => {
  const [actionSettings, setActionSettings] = useState(INITIAL_ACTION_SETTINGS);

  const [isLoading, setIsLoading] = useState(false);

  const { appConfig, isCustomColorsEnabled } = useUserContext();

  const {
    callActions,
    initialCallActions,
    selectedCallAction,
    updateCallActions,
    updateInitialCallActions,
    isCallActionsChanged,
    callActionChanged,
    changeSelectedCallAction,
    changeCallActions,
    closeCallToAction,
    resetCallActions,
    updateCallActionsSeekValue,
    setIsNewCallToAction,
    brandColors,
    availableFonts,
  } = useCallToActionContext();

  const { t } = useTranslation();

  const { videoProgress } = useVideoProgressContext();

  const [touchedFields, setTouchedFields] = useState({ text: false, url: false });

  const selectedHasBackground = useMemo(() => actionSettings?.bgColor && isHexColor(actionSettings?.bgColor) && !checkIsFullyTransparent(actionSettings?.bgColor), [actionSettings]);

  const optionsRef = useRef(null);
  const textAreaRef = useRef(null);


  const type = useMemo(() => actionSettings.fullscreen ? CARD_TYPE : actionSettings?.url !== null ? CALL_TO_ACTION_TYPE : TEXT_BOX_TYPE, [actionSettings]);

  const isInteractive = useMemo(() => type === CALL_TO_ACTION_TYPE, [type]);

  const checkURLFormat = url => {
    const re = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+([/?].*)?$/gm;
    return re.test(url);
  };

  const isValid = useMemo(
    () =>
      callActions?.every(item => {
        if (item.text?.length === 0) {
          return false;
        }
        if (isInteractive && item.id === selectedCallAction.id) {
          if (item.url === null) {
            return false;
          }
          return checkURLFormat(item.url.trim());
        }
        return true;
      }),
    [callActions, isInteractive, selectedCallAction]
  );

  const { isTextValid, isUrlValid } = useMemo(() => {
    const { text, url } = callActions?.find(action => action.id === selectedCallAction.id) || {};

    let isTextValid = true;
    if (touchedFields.text) {
      isTextValid = text?.length > 0;
    }

    let isUrlValid = true;
    if (isInteractive && touchedFields.url && url) {
      isUrlValid = checkURLFormat(url.trim());
    }

    return { isTextValid, isUrlValid };
  }, [callActions, selectedCallAction, touchedFields, isInteractive]);

  useEffect(() => {
    if (checkClosing && !miniSidebar) {
      handleCloseEditor();
    }
  }, [checkClosing]);

  const changeActionSettings = useCallback(
    (changes, registerChanges = true) => {
      setActionSettings(prevState => ({ ...prevState, ...changes }));

      const actionChanges = { ...changes };
      if (changes.bgColor) {
        if (isHexColor(changes.bgColor)) {
          actionChanges.bgColor = changes.bgColor;
        } else {
          actionChanges.bgColor = null;
        }
      }

      if (changes.textColor) {
        actionChanges.textColor = changes.textColor;
      }

      if (changes.position) {
        actionChanges.position = changes.position.type;
      }

      changeCallActions(selectedCallAction.id, actionChanges);

      if (registerChanges) {
        callActionChanged(true);
      }
    },
    [setActionSettings, selectedCallAction]
  );

  const changeCtaType = (type) => {
    switch (type) {
      case TEXT_BOX_TYPE:
        changeActionSettings({ url: null, fullscreen: false, radius: DEFAULT_RADIUS });
        break;
      case CALL_TO_ACTION_TYPE:
        changeActionSettings({ url: '', fullscreen: false, radius: DEFAULT_RADIUS });
        break;
      case CARD_TYPE:
        let position = actionSettings.position;
        if (!actionSettings.position.type.includes('middle')) {
          position = POSITIONS.MIDDLE;
        }
        changeActionSettings({ url: null, fullscreen: true, position, radius: 0 });
        break;
      default:
        break;
    }
  }

  const handleTextColorSelection = useCallback((color) => {
    changeActionSettings({ textColor: color });
  }, [changeActionSettings, actionSettings]);

  const handleBgColorSelection = useCallback((color) => {
    changeActionSettings({ bgColor: color });
  }, [changeActionSettings, actionSettings]);

  const handleCloseEditor = useCallback(async () => {
    if (selectedCallAction.id) {
      if (isCallActionsChanged) {
        const confirmClose = confirm(t('call_to_action.editor.close_confirm'));
        if (confirmClose) {
          callActionChanged(false);
          changeSelectedCallAction({});
          updateCallActions(initialCallActions);
          if (checkClosing) {
            closeCallToAction();
            onCloseEditor();
          }
        }
      } else {
        changeSelectedCallAction({});
      }
    } else {
      callActionChanged(false);
      closeCallToAction();
      await onCloseEditor();
    }
  }, [checkClosing, isCallActionsChanged, selectedCallAction, initialCallActions]);

  const handleSaveActions = async () => {
    try {
      setIsLoading(true);

      const actionToUpdate = callActions.find(({ id }) => id === selectedCallAction?.id);
      actionToUpdate.text = actionToUpdate.text.trim();
      const isExistingAction = initialCallActions.find(({ id }) => id === actionToUpdate?.id);

      // Get URL for changed and new CTAs
      const { url, fields } = await VideoService.getCallActionUploadUrl(storyId);

      const imgBlobUrl = await generateCallActionPng(appConfig, videoDimensions, actionToUpdate);
      const imgFetchResponse = await fetch(imgBlobUrl);
      const imgFile = await imgFetchResponse.blob();
      const callToActionUrl = await VideoService.uploadCallToAction(imgFile, url, fields);
      await VideoService.checkFileInS3(callToActionUrl);

      if (isExistingAction) {
        const { font, ...actionPayload } = actionToUpdate;
        const currentFont = availableFonts?.find(({ id }) => id === font?.id);

        const payload = {
          ...actionPayload,
          ...(currentFont && { fontId: currentFont.id }),
          offset: Math.round(actionPayload.offset),
          duration: Math.round(actionPayload.duration),
          position: actionPayload.position,
          resourceUrl: callToActionUrl,
          url: isInteractive ? actionPayload.url : null,
        };

        if (payload?.bgColor && !isHexColor(payload.bgColor)) {
          payload.bgColor = null;
        }

        await VideoService.updateStoryCallAction(actionToUpdate?.id, payload);
      } else {
        const { id, font, ...actionPayload } = actionToUpdate;
        const currentFont = availableFonts?.find(({ id }) => id === font?.id);

        const payload = {
          ...actionPayload,
          ...(currentFont && { fontId: currentFont.id }),
          offset: Math.round(actionPayload.offset),
          duration: Math.round(actionPayload.duration),
          position: actionPayload.position,
          storyId,
          url: isInteractive ? actionPayload.url : null,
          resourceUrl: callToActionUrl,
        };
        if (payload?.bgColor && !isHexColor(payload.bgColor)) {
          payload.bgColor = null;
        }
        await VideoService.createStoryCallAction(payload);
      }

      const response = await VideoService.getStoryCallActions(storyId);

      if (response) {
        const currentActions = getCurrentActions(response, videoDuration);
        updateCallActions(currentActions);
        updateInitialCallActions(currentActions);

        // Set new created action as current. Needed 'cause created CTA id in not the same as the id the comes from the API
        if (!isExistingAction) {
          const actionIndex = callActions.findIndex(item => item.id === actionToUpdate.id);
        }
      }

      setIsNewCallToAction(false);
      setIsLoading(false);
      callActionChanged(false);
      changeSelectedCallAction({});

      ToastNotification({
        message: t('clip_organizer.toast.successfully_saved'),
        iconName: 'success',
      });
    } catch (err) {
      ToastNotification({
        message: t('clip_organizer.toast.error_while_saving'),
      });
      console.log('Error while requesting call to actions', err);
    } finally {
      setIsLoading(false);
    }
  };

  const onReloadEvent = event => {
    const e = event || window.event;
    // Cancel the event
    e.preventDefault();
    if (e) {
      e.returnValue = ''; // Legacy method for cross browser support
    }
    return ''; // Legacy method for cross browser support
  };

  const handleInput = e => {
    if (e.target.value) {
      e.target.style.height = 'inherit';
      e.target.style.height = `${e.target.scrollHeight + 2}px`;
    }
  };

  const handleKeydown = event => {
    switch (event.keyCode) {
      case BACKSPACE_KEY_CODE: {
        handleInput(event);
        break;
      }
    }
  };

  const handleTitleChange = event => {
    changeActionSettings({ text: event.target?.value.trimStart() });
  };

  const handleSelectCta = item => {
    if (item) {
      changeSelectedCallAction(item);
      updateCallActionsSeekValue((item.offset + STEP_FROM_ACTION_OFFSET) / videoDuration);
    }
  };

  useEffect(() => {
    if (textAreaRef?.current?.value) {
      textAreaRef.current.style.height = 'inherit';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight + 2}px`;
    }
  }, [textAreaRef?.current]);

  useEffect(() => {
    if (callActions?.length) {
      const currentlySelected = callActions?.find(
        item => videoProgress.milliseconds >= item.offset && videoProgress.milliseconds <= item.offset + item.duration
      );
      if (currentlySelected) {
        changeSelectedCallAction(currentlySelected);
      }
    }

    return () => {
      callActionChanged(false);
      changeSelectedCallAction({});
      resetCallActions(false);
    };
  }, []);

  useEffect(() => {
    if (selectedCallAction.id) {
      let bgColor;
      if (selectedCallAction.bgColor === '#FFFFFF00' || checkIsFullyTransparent(selectedCallAction.bgColor)) {
        bgColor = '#FFFFFF00';
      } else {
        bgColor = brandColors.find(({ color, id }) => color === selectedCallAction.bgColor || id === selectedCallAction.bgColor)?.color || brandColors[0]?.color;
      }
      const textColor = fontAdditionalColors[0];
      const position = Object.values(POSITIONS).find(({ type }) => type === selectedCallAction.position) || POSITIONS.TOP_LEFT;
      const font = selectedCallAction.font || availableFonts[0];

      const transformedAction = {
        ...selectedCallAction,
        textColor,
        bgColor,
        position,
        font,
      };

      setActionSettings(transformedAction);
      setTouchedFields({ text: false, url: false });
    }
  }, [selectedCallAction.id]);

  useEffect(() => {
    if (isCallActionsChanged) {
      window.addEventListener('beforeunload', onReloadEvent);
    }

    return () => window.removeEventListener('beforeunload', onReloadEvent);
  }, [isCallActionsChanged]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <h2>{t('text_box.editor.title')}</h2>
          <CloseButton onClose={handleCloseEditor} />
        </div>
        {!selectedCallAction.id && <CallToActionsList ctaList={callActions} onCtaClicked={handleSelectCta} />}
        {!!selectedCallAction.id && (
          <div ref={optionsRef} className={styles.actionsOptions}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className={styles.inputHeader}>{t('my_video.tools.zoom.type')}</span>
              <SelectOption
                parentRef={optionsRef}
                currentlySelected={TYPES.find(({ id }) => type === id)}
                options={TYPES}
                onSelect={({ id }) => changeCtaType(id)}
                currentInputClassName={styles.currentSelect}
              />
            </div>
            <div className={styles.inputContainer}>
              <span className={styles.inputHeader}>{t('call_to_action.editor.button_text')}</span>
              <textarea
                type="text"
                placeholder={t('call_to_action.editor.button_text.placeholder')}
                onChange={handleTitleChange}
                className={cx(styles.nameInput, { [styles.invalidInput]: !isTextValid })}
                ref={textAreaRef}
                value={actionSettings.text}
                onBlur={() => setTouchedFields(prevState => ({ ...prevState, text: true }))}
                maxLength={125}
                onKeyDown={handleKeydown}
                onInput={handleInput}
                onPaste={handleInput}
              />
            </div>
            <div className={cx(styles.cardTextAlignment, { [styles.hide]: type !== CARD_TYPE })}>
              <span className={styles.inputHeader}>{t('text_box.editor.text_alignment')}</span>
              <div className={styles.container}>
                {[POSITIONS.MIDDLE_LEFT, POSITIONS.MIDDLE, POSITIONS.MIDDLE_RIGHT].map((position) => <button className={cx(styles.alignmentBtn, styles[position.type], { [styles.active]: actionSettings.position.type === position.type })} onClick={() => changeActionSettings({ position })} aria-label={t('my_video.tools.call_to_action')}>
                  {[1, 0.5, 0.75].map(width => <div className={styles.alignmentLine} style={{ '--var-width': width }} />)}
                </button>)}
              </div>
            </div>
            <div className={cx(styles.inputContainer, styles.urlInput, { [styles.hide]: !isInteractive })}>
              <span className={styles.inputHeader}>{t('call_to_action.editor.button_link')}</span>
              <input
                type="text"
                className={cx(styles.nameInput, { [styles.invalidInput]: !isUrlValid })}
                value={actionSettings.url || ''}
                placeholder={'https://...'}
                onChange={event => changeActionSettings({ url: event.target?.value.trimStart() })}
                onBlur={() => setTouchedFields(prevState => ({ ...prevState, url: true }))}
              />
            </div>
            <SelectOption
              parentRef={optionsRef}
              title={t('call_to_action.editor.font_settings')}
              currentlySelected={actionSettings.font}
              options={availableFonts}
              onSelect={font => changeActionSettings({ font })}
              currentInputClassName={styles.currentSelect}
            />
            <div className={styles.inputContainer}>
              <span className={styles.inputHeader}>{t('call_to_action.editor.text_color')}</span>
              <BrandingColorsSelection allowCustom={isCustomColorsEnabled} active={actionSettings.textColor} colors={brandColors} additionalColors={fontAdditionalColors} onSelect={handleTextColorSelection} />
            </div>
            <div className={styles.inputContainer}>
              <span className={styles.inputHeader}>{t('call_to_action.editor.button_color')}</span>
              <BrandingColorsSelection allowCustom={isCustomColorsEnabled} active={actionSettings.bgColor} colors={brandColors} additionalColors={['#FFFFFF00']} onSelect={handleBgColorSelection} />
            </div>
            {type !== CARD_TYPE && <SelectOption
              parentRef={optionsRef}
              title={t('call_to_action.editor.button_position')}
              currentlySelected={actionSettings.position}
              options={Object.values(POSITIONS)}
              onSelect={position => changeActionSettings({ position })}
              currentInputClassName={styles.currentSelect}
            />}
            <div className={styles.sliderWrapper}>
              <span className={styles.sliderTitle}>{t('call_to_action.editor.size')}</span>
              <div className={styles.sliderContainer}>
                <span className={styles.minMaxValue}>{MIN_SIZE}</span>
                <Slider
                  min={MIN_SIZE}
                  max={MAX_SIZE}
                  step={1}
                  onChange={value => changeActionSettings({ size: Math.round(value) })}
                  value={actionSettings.size}
                  included
                  style={{ width: '175px' }}
                  handleStyle={{
                    width: '18px',
                    height: '18px',
                    background: '#FFFFFF',
                    position: 'relative',
                    top: '-10px',
                    borderRadius: '9px',
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                    cursor: 'pointer',
                  }}
                  trackStyle={{
                    background: '#37C1A0',
                    height: '4px',
                    borderRadius: '2px',
                    position: 'relative',
                    top: '-4px',
                  }}
                  railStyle={{
                    background: '#EEEEEE',
                    height: '4px',
                    borderRadius: '2px',
                    position: 'relative',
                  }}
                />
                <span className={styles.minMaxValue}>{MAX_SIZE}</span>
                <div className={styles.currentValueContainer}>
                  <div
                    className={styles.currentValue}
                    style={{
                      left: `calc(${((actionSettings.size - MIN_SIZE) / (MAX_SIZE - MIN_SIZE)) * 100}% + 11.5px)`,
                    }}
                  >
                    {actionSettings.size}
                  </div>
                </div>
              </div>
            </div>
            {type !== CARD_TYPE && selectedHasBackground && (
              <div className={styles.sliderWrapper}>
                <span className={styles.sliderTitle}>{t('call_to_action.editor.button_radius')}</span>
                <div className={styles.sliderContainer}>
                  <span className={styles.minMaxValue}>{MIN_RADIUS}</span>
                  <Slider
                    min={MIN_RADIUS}
                    max={MAX_RADIUS}
                    step={1}
                    onChange={value => changeActionSettings({ radius: Math.round(value) })}
                    value={actionSettings.radius}
                    included
                    style={{ width: '175px' }}
                    handleStyle={{
                      width: '18px',
                      height: '18px',
                      background: '#FFFFFF',
                      position: 'relative',
                      top: '-10px',
                      borderRadius: '9px',
                      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                    }}
                    trackStyle={{
                      background: '#37C1A0',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                      top: '-4px',
                    }}
                    railStyle={{
                      background: '#EEEEEE',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                  />
                  <span className={styles.minMaxValue}>{MAX_RADIUS}</span>
                  <div className={styles.currentValueContainer}>
                    <div
                      className={styles.currentValue}
                      style={{
                        left: `calc(${((actionSettings.radius - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100}% + 11.5px)`,
                      }}
                    >
                      {actionSettings.radius}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {selectedCallAction?.id && (
        <div className={styles.btnContainer}>
          <CustomButtonTooltip
            label={t('general.editor.save_changes')}
            variant="primary"
            onClick={handleSaveActions}
            className={styles.saveBtn}
            disabled={!isCallActionsChanged || !isValid}
            loading={isLoading}
            loadingSpinner
            tooltip={isValid ? null : t('call_to_action.editor.invalid_tooltip')}
          />
        </div>
      )}
    </div>
  );
};

export default CallToActionEditor;
